import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../layout/Layout";
import { catalogService } from "../services";
import { SectionTitle, SkeletonGrid } from "../components/ui";

const Carousel = ({ items, renderItem }) => {
  const trackRef = useRef(null);
  const scrollBy = (offset) => trackRef.current?.scrollBy({ left: offset, behavior: "smooth" });
  return (
    <div className="lt-carousel">
      <div className="lt-carousel__track" ref={trackRef}>
        {items.map(renderItem)}
      </div>
      <div className="lt-flex lt-gap-8" style={{ justifyContent: "flex-end", marginTop: 10 }}>
        <button type="button" className="lt-carousel__btn" onClick={() => scrollBy(-540)}>
          ‹
        </button>
        <button type="button" className="lt-carousel__btn" onClick={() => scrollBy(540)}>
          ›
        </button>
      </div>
    </div>
  );
};

const Tile = ({ to, image, name, badge, available = true }) => (
  <Link to={to} className="lt-tile">
    <div className="lt-tile__image">
      <img src={image} alt={name} loading="lazy" />
      {badge ? <span className="lt-tile__badge">{badge}</span> : null}
      {!available ? <div className="lt-tile__off">ปิดปรับปรุง</div> : null}
    </div>
    <div className="lt-tile__name">{name}</div>
  </Link>
);

export default function Home() {
  const [shortcuts, setShortcuts] = useState([]);
  const [games, setGames] = useState(null);
  const [cards, setCards] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [channels, setChannels] = useState([]);

  useEffect(() => {
    let alive = true;
    Promise.all([
      catalogService.shortcuts(),
      catalogService.games(),
      catalogService.cards(),
      catalogService.promotions(),
      catalogService.orderPaymentChannels(),
    ]).then(([s, g, c, promo, ch]) => {
      if (!alive) return;
      setShortcuts(s || []);
      setGames(g || []);
      setCards(c || []);
      setPromotions(promo || []);
      setChannels(ch || []);
    });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <Layout announce>
      <section className="lt-hero">
        <span className="lt-hero__glow lt-hero__glow--one" />
        <span className="lt-hero__glow lt-hero__glow--two" />
        <div className="lucid-container lt-hero__inner">
          <span className="lt-hero__eyebrow">Top-up experience</span>
          <h1 className="lt-hero__title">
            บริการเติมเกม <span>LucidTopup</span>
          </h1>
          <p className="lt-hero__desc">
            เติมเกมออนไลน์ เติมเงินมือถือ และจำหน่ายบัตรเติมเงินทุกชนิด ด้วยระบบอัตโนมัติตลอด 24 ชั่วโมง
          </p>
          <div className="lt-hero__badges">
            <span>สะดวก</span>
            <span>รวดเร็ว</span>
            <span>ปลอดภัย</span>
          </div>
        </div>
      </section>

      <div className="lucid-container">
        <div className="lt-shortcuts">
          {shortcuts.map((item) => (
            <Link key={item.id} to={item.to} className="lt-shortcut">
              <div className="lt-shortcut__thumbs">
                {item.images.map((src, index) => (
                  <img key={index} src={src} alt="" loading="lazy" />
                ))}
              </div>
              <div>
                <div className="lt-shortcut__title">{item.title}</div>
                <div className="lt-shortcut__subtitle">{item.subtitle}</div>
              </div>
            </Link>
          ))}
        </div>

        <div className="lt-text-center lt-mt-24">
          <p className="lt-text-muted lt-mb-0">ช่องทางการเติมเงิน</p>
          <div className="lt-paychannels">
            {channels.map((channel) => (
              <div className="lt-paychannels__item" key={channel.id}>
                <span>{channel.logoText}</span>
                <small>{channel.logoSub}</small>
              </div>
            ))}
          </div>
        </div>

        <SectionTitle
          title="เติมเกม"
          subtitle="เลือกเกมยอดนิยม พร้อมเติมได้ทันที"
          action={
            <Link to="/topup/game" className="lt-btn lt-btn--navy lt-btn--pill">
              ดูทั้งหมด
            </Link>
          }
        />
        {games === null ? (
          <SkeletonGrid count={6} height={200} />
        ) : (
          <Carousel
            items={games.filter((game) => game.popular).slice(0, 14)}
            renderItem={(game) => (
              <Tile
                key={game.id}
                to={`/topup/game/${game.id}/${game.slug}`}
                image={game.image}
                name={game.name}
                badge={game.promo ? "PROMO" : null}
                available={game.available}
              />
            )}
          />
        )}

        <SectionTitle
          title="บัตรเติมเงิน"
          subtitle="รวมบัตรเติมเงินและคูปองยอดนิยม"
          action={
            <Link to="/topup/card" className="lt-btn lt-btn--navy lt-btn--pill">
              ดูทั้งหมด
            </Link>
          }
        />
        <Carousel
          items={cards}
          renderItem={(card) => (
            <Tile key={card.id} to={`/topup/card/${card.id}/${card.slug}`} image={card.image} name={card.name} />
          )}
        />

        <SectionTitle
          title="โปรโมชั่น"
          subtitle="ข่าวสารและสิทธิพิเศษล่าสุด"
          action={
            <Link to="/promotions" className="lt-btn lt-btn--navy lt-btn--pill">
              ดูทั้งหมด
            </Link>
          }
        />
        <div className="lt-promo-grid" style={{ marginBottom: 20 }}>
          {promotions.slice(0, 3).map((promo) => (
            <article className="lt-promo" key={promo.id}>
              <img className="lt-promo__cover" src={promo.cover} alt={promo.title} loading="lazy" />
              <div className="lt-promo__body">
                <div className="lt-promo__title">🌈 {promo.title}</div>
                <div className="lt-promo__meta">Update {promo.updatedAt}</div>
                <Link to={`/promotions/detail/${promo.id}`} className="lt-btn lt-btn--ghost lt-btn--pill">
                  อ่านเพิ่มเติม
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </Layout>
  );
}
